import type { Bounds, Vec2 } from '@/types';
import { boundsCenter } from '@/core/geometry/bounds';

/** Where a centre-to-centre line leaves an axis-aligned box, so arrowheads sit on the edge. */
export const boundaryPoint = (box: Bounds, towards: Vec2): Vec2 => {
  const c = boundsCenter(box);
  const dx = towards.x - c.x;
  const dy = towards.y - c.y;
  if (dx === 0 && dy === 0) return c;
  const hw = (box.maxX - box.minX) / 2;
  const hh = (box.maxY - box.minY) / 2;
  const sx = dx === 0 ? Infinity : hw / Math.abs(dx);
  const sy = dy === 0 ? Infinity : hh / Math.abs(dy);
  const scale = Math.min(sx, sy);
  return { x: c.x + dx * scale, y: c.y + dy * scale };
};

/** Segment endpoints for a connection between two boxes, trimmed to their edges. */
export const connectionSegment = (source: Bounds, target: Bounds): { readonly a: Vec2; readonly b: Vec2 } => {
  const sc = boundsCenter(source);
  const tc = boundsCenter(target);
  return { a: boundaryPoint(source, tc), b: boundaryPoint(target, sc) };
};

/** Distance from a point to a line segment. */
export const distanceToSegment = (p: Vec2, a: Vec2, b: Vec2): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};

export const midpoint = (a: Vec2, b: Vec2): Vec2 => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
